
import { useState } from "react";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { FormItem } from "@/components/ui/form";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Upload, Camera, Calendar, Phone, Mail, User as UserIcon } from "lucide-react";

interface ProfileFormPersonalProps {
  formData: {
    nome: string;
    email: string;
    telefone: string;
    dataNascimento: string;
    bio: string;
    profilePicture?: string;
  };
  handleChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  handleImageUpload: (file: File) => void;
}

export const ProfileFormPersonal = ({ formData, handleChange, handleImageUpload }: ProfileFormPersonalProps) => {
  const [isDragging, setIsDragging] = useState(false);
  
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      handleImageUpload(file);
    }
  };
  
  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(true);
  };
  
  const handleDragLeave = () => {
    setIsDragging(false);
  };
  
  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);
    
    const file = e.dataTransfer.files?.[0];
    if (file) {
      handleImageUpload(file);
    }
  };

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Foto de perfil */}
      <div className="flex flex-col items-center mb-8">
        <div
          className={`relative group cursor-pointer mb-4 ${isDragging ? 'ring-4 ring-primary/30' : ''}`}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
        >
          <Avatar className="h-32 w-32 border-4 border-white shadow-lg">
            <AvatarImage src={formData.profilePicture || ""} alt={formData.nome} />
            <AvatarFallback className="bg-primary text-white text-2xl">
              {formData.nome ? formData.nome.substring(0, 2).toUpperCase() : "US"}
            </AvatarFallback>
          </Avatar>
          <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-60 rounded-full opacity-0 group-hover:opacity-100 transition-opacity">
            <Camera className="text-white h-8 w-8" />
          </div>
        </div>
        
        <div className="flex flex-col items-center">
          <label htmlFor="profile-picture" className="cursor-pointer">
            <Button type="button" variant="outline" size="sm" className="flex items-center gap-2 bg-white border-primary text-primary hover:bg-primary/5">
              <Upload className="h-4 w-4" />
              Alterar foto
            </Button>
            <input 
              id="profile-picture" 
              type="file" 
              accept="image/*" 
              className="hidden" 
              onChange={handleFileChange}
            />
          </label>
          <p className="text-xs text-gray-500 mt-1">Arraste uma imagem ou clique para selecionar</p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <FormItem className="space-y-2">
          <Label htmlFor="nome" className="text-gray-700 font-medium flex items-center gap-2">
            <UserIcon className="h-4 w-4 text-primary/70" />
            Nome Completo
          </Label>
          <Input 
            id="nome" 
            value={formData.nome} 
            onChange={handleChange} 
            className="border border-gray-300 focus:border-primary focus:ring focus:ring-primary/20" 
            required 
            placeholder="Digite seu nome completo"
          />
        </FormItem>
        <FormItem className="space-y-2">
          <Label htmlFor="email" className="text-gray-700 font-medium flex items-center gap-2">
            <Mail className="h-4 w-4 text-primary/70" />
            E-mail
          </Label>
          <Input 
            id="email" 
            type="email" 
            value={formData.email} 
            onChange={handleChange} 
            className="border border-gray-300 focus:border-primary focus:ring focus:ring-primary/20" 
            required 
            placeholder="seu.email@exemplo.com"
          />
        </FormItem>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <FormItem className="space-y-2">
          <Label htmlFor="telefone" className="text-gray-700 font-medium flex items-center gap-2">
            <Phone className="h-4 w-4 text-primary/70" />
            Telefone
          </Label>
          <Input 
            id="telefone" 
            value={formData.telefone} 
            onChange={handleChange}
            className="border border-gray-300 focus:border-primary focus:ring focus:ring-primary/20" 
            placeholder="(00) 00000-0000" 
          />
        </FormItem>
        <FormItem className="space-y-2">
          <Label htmlFor="dataNascimento" className="text-gray-700 font-medium flex items-center gap-2">
            <Calendar className="h-4 w-4 text-primary/70" />
            Data de Nascimento
          </Label>
          <Input 
            id="dataNascimento" 
            type="date" 
            value={formData.dataNascimento} 
            onChange={handleChange}
            className="border border-gray-300 focus:border-primary focus:ring focus:ring-primary/20" 
          />
        </FormItem>
      </div>
      
      <FormItem className="space-y-2">
        <Label htmlFor="bio" className="text-gray-700 font-medium">Biografia</Label>
        <Textarea 
          id="bio" 
          value={formData.bio} 
          onChange={handleChange}
          className="min-h-[120px] border border-gray-300 focus:border-primary focus:ring focus:ring-primary/20" 
          placeholder="Conte um pouco sobre você..."
          rows={4} 
        />
      </FormItem>
    </div>
  );
};
